import { z } from 'zod';
import { OpportunityControlStateSchema } from './api-handoff.js';
import { IncidentKindSchema, IncidentStateSchema } from './api-incidents.js';

// Supervision (v1 §10.9, mapa de producto «Supervisar e intervenir», ficha del proyecto): critical alerts first, «Tomar
// caso» as a temporary intervention (LIA stops contacting the opportunity until resumed with a note + next step —
// distinct from the definitive handoff), and the project's three evaluation blocks. Every number is a real count over
// stored facts (test contacts excluded); a block without data says so instead of showing 0.

const Id = z.string().min(1).max(64);
const Iso = z.string();

/** An open incident as an alert row (home + ficha): project, reason, waiting time, who has it or «sin tomar». */
export const CaseAlertSchema = z.object({
  incidentId: Id,
  projectId: Id,
  projectName: z.string(),
  kind: IncidentKindSchema,
  state: IncidentStateSchema,
  critical: z.boolean(),
  reason: z.string(),
  contactName: z.string().nullable(),
  leadInterestId: Id.nullable(),
  ownerName: z.string().nullable(),
  /** Someone confirmed they have it (acknowledged / took the case). */
  taken: z.boolean(),
  /** Deadline to take it; null once taken. */
  takeDueAt: Iso.nullable(),
  overdue: z.boolean(),
  waitingMinutes: z.number().int(),
  createdAt: Iso,
});
export type CaseAlert = z.infer<typeof CaseAlertSchema>;

/** Per-project summary on the home (last 30 days). */
export const HomeProjectSummarySchema = z.object({
  projectId: Id,
  projectName: z.string(),
  bookedByLia: z.number().int(),
  held: z.number().int(),
  openAlerts: z.number().int(),
  criticalAlerts: z.number().int(),
});
export type HomeProjectSummary = z.infer<typeof HomeProjectSummarySchema>;

export const TakeCaseSchema = z.object({
  /** The alert this take answers (acknowledged and assigned to the person). Default: every active alert of the opportunity. */
  incidentId: Id.optional(),
  reason: z.string().trim().max(500).optional(),
});
export type TakeCase = z.infer<typeof TakeCaseSchema>;

export const ResumeCaseSchema = z.object({
  note: z.string().trim().min(3).max(1000),
  nextStep: z.string().trim().min(3).max(300),
  nextStepAt: Iso.optional(),
});
export type ResumeCase = z.infer<typeof ResumeCaseSchema>;

export const CaseControlSchema = z.object({
  leadInterestId: Id,
  state: OpportunityControlStateSchema,
  takenAt: Iso.nullable(),
  takenByName: z.string().nullable(),
  takenReason: z.string().nullable(),
  resumedAt: Iso.nullable(),
  resumedByName: z.string().nullable(),
  resumeNote: z.string().nullable(),
  resumeNextStep: z.string().nullable(),
});
export type CaseControl = z.infer<typeof CaseControlSchema>;

// ───────── Ficha del proyecto: evaluation ─────────

export const ProjectEvaluationQuerySchema = z.object({ days: z.coerce.number().int().min(7).max(90).default(30) });

/** A case behind an indicator (opens the lead view). */
export const EvaluationCaseSchema = z.object({
  leadInterestId: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  detail: z.string().nullable(),
  at: Iso.nullable(),
});
export type EvaluationCase = z.infer<typeof EvaluationCaseSchema>;

export const PROJECT_ATTENTION_KINDS = ['alert', 'handoff', 'taken_case'] as const;
export const ProjectAttentionSchema = z.object({
  kind: z.enum(PROJECT_ATTENTION_KINDS),
  title: z.string(),
  detail: z.string().nullable(),
  ownerName: z.string().nullable(),
  waitingMinutes: z.number().int(),
  critical: z.boolean(),
  leadInterestId: Id.nullable(),
  incidentId: Id.nullable(),
  /** «Tomar caso» available (an AI-active opportunity). */
  canTake: z.boolean(),
  /** Taken case of this opportunity: can be resumed with a note + next step. */
  canResume: z.boolean(),
});
export type ProjectAttention = z.infer<typeof ProjectAttentionSchema>;

export const QualityFindingSchema = z.object({
  auditId: Id,
  conversationId: Id.nullable(),
  contactName: z.string().nullable(),
  /** LIA's own conversation (no advisor audited) vs. an advisor's. */
  byLia: z.boolean(),
  dimension: z.string(),
  severity: z.string(),
  text: z.string(),
  evidence: z.array(z.object({ messageId: z.string().nullable(), quote: z.string() })),
});
export type QualityFinding = z.infer<typeof QualityFindingSchema>;

export const ProjectEvaluationSchema = z.object({
  projectId: Id,
  projectName: z.string(),
  from: Iso,
  to: Iso,
  days: z.number().int(),
  attention: z.array(ProjectAttentionSchema),
  /** Operational compliance: LIA's tasks (setter tasks, test records excluded). */
  compliance: z.object({
    dueNext24h: z.number().int(),
    overdue: z.number().int(),
    missingNext: z.number().int(),
    closed: z.number().int(),
    closedOnTime: z.number().int(),
    closedLate: z.number().int(),
    /** null without closed tasks in the period (unknown ≠ 0). */
    onTimeRate: z.number().nullable(),
    cases: z.object({ overdue: z.array(EvaluationCaseSchema), missingNext: z.array(EvaluationCaseSchema), late: z.array(EvaluationCaseSchema) }),
  }),
  /** Commercial results backed by stored facts (bookings confirmed by the calendar, outcomes reported by the team/CRM). */
  results: z.object({
    assigned: z.number().int(),
    withVisitHeld: z.number().int(),
    /** Leads with at least one held visit per 100 assigned; null when nothing was assigned. */
    per100: z.number().nullable(),
    bookedByLia: z.number().int(),
    held: z.number().int(),
    noShow: z.number().int(),
    cancelled: z.number().int(),
    funnel: z.array(z.object({ stage: z.string(), count: z.number().int() })),
    cases: z.object({ bookedByLia: z.array(EvaluationCaseSchema), held: z.array(EvaluationCaseSchema) }),
  }),
  /** Conversation quality: findings of the evaluated conversations with the fragments that support them. */
  quality: z.object({
    evaluated: z.number().int(),
    findings: z.array(QualityFindingSchema),
    note: z.string().nullable(),
  }),
});
export type ProjectEvaluation = z.infer<typeof ProjectEvaluationSchema>;
